import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import {
    PlusCircle,
    ChevronRight,
    Crown,
    Building2,
    Users,
    Briefcase,
    Shield,
    Search,
    Trash2,
    Link,
    Unlink,
    SquarePen,
    Copy,
} from "lucide-react";
import { Badge } from "./ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "./ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "./ui/popover";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";

// --- Type Definitions (should match your backend entities) ---
interface Resource {
    id: number;
    name: string;
    description?: string;
    category?: string;
}

interface Permission {
    id: number;
    action: string;
    resource: Resource;
    description?: string | null;
}

interface Policy {
    id: number;
    name: string;
    description: string;
    attributeKey: string;
    attributeValue: string;
    permissions: Permission[];
}

// --- Helpers ---
const getPolicyIcon = (name: string) => {
    const lower = name.toLowerCase();
    if (lower.includes("owner")) return <Crown className="h-4 w-4 text-muted-foreground" />;
    if (lower.includes("project")) return <Briefcase className="h-4 w-4 text-muted-foreground" />;
    if (lower.includes("admin") || lower.includes("director") || lower.includes("manager")) {
        return <Building2 className="h-4 w-4 text-muted-foreground" />;
    }
    return <Users className="h-4 w-4 text-muted-foreground" />;
};

const formatPermissionLabel = (permission: Permission): string => {
    const action = permission.action.charAt(0).toUpperCase() + permission.action.slice(1);
    const resource = permission.resource.name.replace(/_/g, " ");
    return `${action} ${resource}`;
};

const formatPermissionCount = (policy: Policy): string => {
    const count = policy.permissions?.length ?? 0;
    return `${count} permission${count === 1 ? "" : "s"}`;
};

// --- Main Component ---
const AbacManagementPage = () => {
    // --- State Management ---
    const { currentUser } = useAuth();
    const { fetchUserRoles } = useData();
    const [policies, setPolicies] = useState<Policy[]>([]);
    const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
    const [dependencies, setDependencies] = useState<Record<number, number[]>>({});
    const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchPermissions, setSearchPermissions] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newPolicyData, setNewPolicyData] = useState({
        name: "",
        description: "",
        attributeKey: "userRole",
        attributeValue: "",
    });
    // Optional template: id of an existing policy whose permissions seed the new policy
    const [copyFromPolicyId, setCopyFromPolicyId] = useState("");
    const [copyFromPopoverOpen, setCopyFromPopoverOpen] = useState(false);
    const [isCreatingPolicy, setIsCreatingPolicy] = useState(false);

    // Attribute value dropdown state
    const [attributeValuePopoverOpen, setAttributeValuePopoverOpen] = useState(false);
    const [isNewAttributeValue, setIsNewAttributeValue] = useState(false);
    const [newAttributeValueInput, setNewAttributeValueInput] = useState("");

    // Existing roles for dropdowns
    const [existingUserRoles, setExistingUserRoles] = useState<string[]>([]);
    const [existingJobRoles, setExistingJobRoles] = useState<string[]>([]);

    // Batch edit state
    const [editedPermissionIds, setEditedPermissionIds] = useState<Set<number>>(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const protectedPolicies = ["Policy: Account Owner", "Policy: Workspace Admin", "Policy: Director"];

    // Add Permission modal state
    const [resources, setResources] = useState<Resource[]>([]);
    const [actions, setActions] = useState<string[]>([]);
    const [isAddPermissionOpen, setIsAddPermissionOpen] = useState(false);
    const [isAddingPermission, setIsAddingPermission] = useState(false);
    
    // Dependency modal state
    const [isDependencyModalOpen, setIsDependencyModalOpen] = useState(false);
    const [dependencySelectPopoverOpen, setDependencySelectPopoverOpen] = useState(false);
    const [activePermissionId, setActivePermissionId] = useState<number | null>(null);
    const [newDependencyId, setNewDependencyId] = useState("");
    const [isSavingDependency, setIsSavingDependency] = useState(false);

    // Edit description modal state
    const [isEditDescriptionOpen, setIsEditDescriptionOpen] = useState(false);
    const [editDescriptionPermission, setEditDescriptionPermission] = useState<Permission | null>(null);
    const [editDescriptionText, setEditDescriptionText] = useState("");
    const [isSavingDescription, setIsSavingDescription] = useState(false);

    const [addPermissionForm, setAddPermissionForm] = useState({
        action: "",
        resourceId: "",
        newResourceName: "",
        newResourceDescription: "",
        category: "",
        newCategory: "",
        newAction: "",
        permissionDescription: "",
    });
    const [actionPopoverOpen, setActionPopoverOpen] = useState(false);
    const [resourcePopoverOpen, setResourcePopoverOpen] = useState(false);

    // --- Helper function for authenticated fetch with base URL ---
    const authenticatedFetch = (urlPath: string, options: RequestInit = {}) => {
        const token = localStorage.getItem("token");
        const apiBaseUrl = `${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/abac/`;
        const fullUrl = `${apiBaseUrl}${urlPath}`;

        const headers = {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            ...options.headers,
        };
        return fetch(fullUrl, { ...options, headers });
    };

    // --- Data Fetching ---
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [policiesRes, permissionsRes, resourcesRes, actionsRes, userRolesRes, jobRolesRes, dependenciesRes] = await Promise.all([
                    authenticatedFetch("policies"),
                    authenticatedFetch("permissions"),
                    authenticatedFetch("resources"),
                    authenticatedFetch("permissions/actions"),
                    fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/users/roles`, {
                        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" }
                    }),
                    fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/jobroles?archived=false&limit=50`, {
                        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" }
                    }),
                    authenticatedFetch("dependencies"),
                ]);

                if (!policiesRes.ok || !permissionsRes.ok) {
                    throw new Error('Failed to fetch initial data');
                }

                const policiesData = await policiesRes.json();
                const permissionsData = await permissionsRes.json();

                if (policiesData.success) setPolicies(policiesData.policies);
                if (permissionsData.success) setAllPermissions(permissionsData.permissions);

                if (dependenciesRes.ok) {
                    const dependenciesData = await dependenciesRes.json();
                    if (dependenciesData.success) {
                        const depMap: Record<number, number[]> = {};
                        dependenciesData.dependencies.forEach((d: any) => {
                            depMap[d.id] = d.dependsOn.map((dep: any) => dep.id);
                        });
                        setDependencies(depMap);
                    }
                }

                if (resourcesRes.ok) {
                    const resourcesData = await resourcesRes.json();
                    setResources(Array.isArray(resourcesData) ? resourcesData : resourcesData.resources || []);
                }

                if (actionsRes.ok) {
                    const actionsData = await actionsRes.json();
                    if (actionsData.success) setActions(actionsData.actions);
                }

                if (userRolesRes.ok) {
                    const userRolesData = await userRolesRes.json();
                    if (userRolesData.success) setExistingUserRoles(userRolesData.userRoles || []);
                }

                if (jobRolesRes.ok) {
                    const jobRolesData = await jobRolesRes.json();
                    const roles = (jobRolesData.roles || []).map((r: any) => r.roleName).filter(Boolean) as string[];
                    setExistingJobRoles([...new Set(roles)]);
                }
            } catch (error) {
                console.error("Failed to fetch ABAC data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    // Reset edited permissions when selected policy changes
    useEffect(() => {
        if (selectedPolicy) {
            const initialIds = new Set(selectedPolicy.permissions.map(p => p.id));

            // Enforce dynamic dependencies: recursively add all dependencies
            const resolveDependencies = (id: number) => {
                const deps = dependencies[id];
                if (deps) {
                    deps.forEach(depId => {
                        if (!initialIds.has(depId)) {
                            initialIds.add(depId);
                            resolveDependencies(depId);
                        }
                    });
                }
            };

            Array.from(initialIds).forEach(resolveDependencies);
            setEditedPermissionIds(initialIds);
        } else {
            setEditedPermissionIds(new Set());
        }
    }, [selectedPolicy, dependencies]);

    // Reset attribute value when attributeKey changes
    useEffect(() => {
        // 'all' policies always use the reserved value 'all' (applies to every user)
        setNewPolicyData(prev => ({ ...prev, attributeValue: prev.attributeKey === 'all' ? 'all' : "" }));
        setIsNewAttributeValue(false);
        setNewAttributeValueInput("");
    }, [newPolicyData.attributeKey]);

    // --- Memoized ---
    const lockedPermissions = useMemo(() => {
        const locked = new Set<number>();
        editedPermissionIds.forEach(id => {
            const deps = dependencies[id];
            if (deps) {
                deps.forEach(depId => locked.add(depId));
            }
        });
        return locked;
    }, [editedPermissionIds, dependencies]);

    const existingCategories = useMemo(() => {
        const cats = new Set(resources.map(r => r.category).filter((c): c is string => !!c));
        return Array.from(cats).sort();
    }, [resources]);

    const isNewResource = addPermissionForm.resourceId === "__new__";
    const isNewCategory = addPermissionForm.category === "__new__";
    const isNewAction = addPermissionForm.action === "__new__";

    // --- Event Handlers ---
    const handleSelectPolicy = async (policyId: number) => {
        try {
            const res = await authenticatedFetch(`policies/${policyId}`);
            if (!res.ok) throw new Error('Failed to fetch policy details');
            const fullPolicyData = await res.json();
            setSelectedPolicy(fullPolicyData.policy);
        } catch (error) {
            console.error(error);
        }
    };

    const handlePermissionToggle = (permissionId: number, isChecked: boolean) => {
        setEditedPermissionIds(prev => {
            const next = new Set(prev);
            if (isChecked) {
                next.add(permissionId);

                // Recursively enable all dependencies
                const addDependencies = (id: number) => {
                    const deps = dependencies[id];
                    if (deps) {
                        deps.forEach(depId => {
                            next.add(depId);
                            addDependencies(depId);
                        });
                    }
                };
                addDependencies(permissionId);
            } else {
                next.delete(permissionId);
            }
            return next;
        });
    };

    const handleSaveChanges = async () => {
        if (!selectedPolicy) return;

        const originalIds = new Set(selectedPolicy.permissions.map(p => p.id));
        const hasChanges =
            editedPermissionIds.size !== originalIds.size ||
            Array.from(editedPermissionIds).some(id => !originalIds.has(id));

        if (!hasChanges) {
            toast.info("No changes to save");
            return;
        }

        setIsSaving(true);
        try {
            const res = await authenticatedFetch(`policies/${selectedPolicy.id}/permissions/bulk`, {
                method: 'POST',
                body: JSON.stringify({ permissionIds: Array.from(editedPermissionIds) }),
            });

            if (!res.ok) {
                throw new Error('Failed to save permissions');
            }

            const data = await res.json();
            if (data.success) {
                setSelectedPolicy(data.policy);
            }

            // Refresh policies list
            const policiesRes = await authenticatedFetch("policies");
            if (policiesRes.ok) {
                const policiesData = await policiesRes.json();
                if (policiesData.success) setPolicies(policiesData.policies);
            }

            toast.success("Changes saved successfully");
        } catch (error) {
            console.error("Failed to save changes:", error);
            toast.error("Failed to save changes");
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        if (selectedPolicy) {
            setEditedPermissionIds(new Set(selectedPolicy.permissions.map(p => p.id)));
        }
    };

    const finalAttributeValue = isNewAttributeValue ? newAttributeValueInput.trim() : newPolicyData.attributeValue;

    const attributeValueOptions = useMemo(() => {
        if (newPolicyData.attributeKey === 'userRole') {
            return existingUserRoles;
        }
        return existingJobRoles;
    }, [newPolicyData.attributeKey, existingUserRoles, existingJobRoles]);

    const selectedCopySource = policies.find(p => String(p.id) === copyFromPolicyId);

    const handleDeletePolicy = async () => {
        if (!selectedPolicy) return;
        setIsDeleting(true);
        try {
            const res = await authenticatedFetch(`policies/${selectedPolicy.id}`, {
                method: "DELETE",
            });
            if (!res.ok) throw new Error('Failed to delete policy');
            setPolicies(prev => prev.filter(p => p.id !== selectedPolicy.id));
            setSelectedPolicy(null);
            setIsDeleteDialogOpen(false);
            toast.success("Policy deleted successfully");
            // Refresh user roles if a userRole policy was deleted
            if (selectedPolicy.attributeKey === 'userRole') {
                fetchUserRoles();
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete policy");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCreatePolicy = async () => {
        if (isCreatingPolicy) return;
        const attrValue = finalAttributeValue;
        if (!attrValue) {
            toast.error("Attribute value is required");
            return;
        }

        const trimmedName = newPolicyData.name.trim();
        if (!trimmedName) {
            toast.error("Name is required");
            return;
        }

        // Check for duplicate policy (same attributeKey + attributeValue)
        const duplicate = policies.find(p =>
            p.attributeKey === newPolicyData.attributeKey &&
            p.attributeValue === attrValue
        );
        if (duplicate) {
            toast.error(`A policy with ${newPolicyData.attributeKey} = "${attrValue}" already exists`);
            return;
        }

        // Policy names are unique in the database — surface the clash here
        // instead of letting the insert fail.
        if (policies.some(p => p.name.toLowerCase() === trimmedName.toLowerCase())) {
            toast.error(`A policy named "${trimmedName}" already exists`);
            return;
        }

        try {
            setIsCreatingPolicy(true);
            const res = await authenticatedFetch('policies', {
                method: "POST",
                body: JSON.stringify({
                    ...newPolicyData,
                    name: trimmedName,
                    attributeValue: attrValue,
                    createdById: currentUser?.id,
                    copyFromPolicyId: copyFromPolicyId ? parseInt(copyFromPolicyId) : undefined,
                }),
            });
            if (!res.ok) throw new Error('Failed to create policy');
            const createdPolicyData = await res.json();
            setPolicies([...policies, createdPolicyData.policy]);
            setIsModalOpen(false);
            resetNewPolicyForm();
            toast.success(
                copyFromPolicyId
                    ? "Policy created and permissions copied"
                    : "Policy created successfully"
            );
            // Open the new policy so its (copied) permissions can be adjusted right away
            setSelectedPolicy(createdPolicyData.policy);
            // Refresh user roles so new userRole policies appear in dropdowns immediately
            if (newPolicyData.attributeKey === 'userRole') {
                fetchUserRoles();
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to create policy");
        } finally {
            setIsCreatingPolicy(false);
        }
    };

    const resetNewPolicyForm = () => {
        setNewPolicyData({ name: "", description: "", attributeKey: "userRole", attributeValue: "" });
        setIsNewAttributeValue(false);
        setNewAttributeValueInput("");
        setCopyFromPolicyId("");
        setCopyFromPopoverOpen(false);
    };

    // Opens the create dialog pre-seeded with an existing policy as the template.
    const handleCopyPolicy = (policy: Policy) => {
        setNewPolicyData({
            name: `${policy.name} (Copy)`,
            description: policy.description || "",
            attributeKey: policy.attributeKey,
            attributeValue: policy.attributeKey === 'all' ? 'all' : "",
        });
        setIsNewAttributeValue(false);
        setNewAttributeValueInput("");
        setCopyFromPolicyId(String(policy.id));
        setCopyFromPopoverOpen(false);
        setIsModalOpen(true);
    };

    const resetAddPermissionForm = () => {
        setAddPermissionForm({
            action: "",
            resourceId: "",
            newResourceName: "",
            newResourceDescription: "",
            category: "",
            newCategory: "",
            newAction: "",
            permissionDescription: "",
        });
        setActionPopoverOpen(false);
        setResourcePopoverOpen(false);
    };

    const handleAddPermission = async () => {
        if (!addPermissionForm.action) {
            toast.error("Please select an action");
            return;
        }
        if (isNewAction && !addPermissionForm.newAction.trim()) {
            toast.error("Please enter an action name");
            return;
        }
        if (!addPermissionForm.resourceId) {
            toast.error("Please select a resource");
            return;
        }
        if (isNewResource && !addPermissionForm.newResourceName.trim()) {
            toast.error("Please enter a resource name");
            return;
        }
        if (isNewResource && isNewCategory && !addPermissionForm.newCategory.trim()) {
            toast.error("Please enter a category name");
            return;
        }

        setIsAddingPermission(true);
        try {
            let resourceId = addPermissionForm.resourceId;

            // Create new resource if needed
            if (isNewResource) {
                const category = isNewCategory
                    ? addPermissionForm.newCategory.trim()
                    : addPermissionForm.category;

                const resourceRes = await authenticatedFetch("resources", {
                    method: "POST",
                    body: JSON.stringify({
                        name: addPermissionForm.newResourceName.trim(),
                        description: addPermissionForm.newResourceDescription.trim() || undefined,
                        category: category || undefined,
                    }),
                });

                if (!resourceRes.ok) {
                    const errData = await resourceRes.json().catch(() => ({}));
                    throw new Error(errData.message || "Failed to create resource");
                }

                const resourceData = await resourceRes.json();
                resourceId = String(resourceData.resource.id);

                // Update local resources list
                setResources(prev => [...prev, resourceData.resource]);
            }

            // Create the permission
            const permRes = await authenticatedFetch("permissions", {
                method: "POST",
                body: JSON.stringify({
                    action: isNewAction ? addPermissionForm.newAction.trim() : addPermissionForm.action,
                    resourceId: parseInt(resourceId),
                    description: addPermissionForm.permissionDescription.trim() || undefined,
                }),
            });

            if (!permRes.ok) {
                const errData = await permRes.json().catch(() => ({}));
                throw new Error(errData.message || "Failed to create permission");
            }

            const permData = await permRes.json();

            // Refresh permissions list
            const allPermsRes = await authenticatedFetch("permissions");
            if (allPermsRes.ok) {
                const allPermsData = await allPermsRes.json();
                if (allPermsData.success) setAllPermissions(allPermsData.permissions);
            }

            toast.success("Permission added successfully");
            setIsAddPermissionOpen(false);
            resetAddPermissionForm();
        } catch (error: any) {
            console.error("Failed to add permission:", error);
            toast.error(error.message || "Failed to add permission");
        } finally {
            setIsAddingPermission(false);
        }
    };

    const handleAddDependency = async () => {
        if (!activePermissionId || !newDependencyId) {
            toast.error("Please select a dependency");
            return;
        }

        setIsSavingDependency(true);
        try {
            const res = await authenticatedFetch("dependencies", {
                method: "POST",
                body: JSON.stringify({
                    permissionId: activePermissionId,
                    dependsOnId: parseInt(newDependencyId)
                }),
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.message || "Failed to add dependency");
            }

            // Refresh dependencies
            const dependenciesRes = await authenticatedFetch("dependencies");
            if (dependenciesRes.ok) {
                const dependenciesData = await dependenciesRes.json();
                const depMap: Record<number, number[]> = {};
                dependenciesData.dependencies.forEach((d: any) => {
                    depMap[d.id] = d.dependsOn.map((dep: any) => dep.id);
                });
                setDependencies(depMap);
            }

            toast.success("Dependency added successfully");
            setNewDependencyId("");
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsSavingDependency(false);
        }
    };

    const handleRemoveDependency = async (permissionId: number, dependsOnId: number) => {
        try {
            const res = await authenticatedFetch("dependencies", {
                method: "DELETE",
                body: JSON.stringify({ permissionId, dependsOnId }),
            });

            if (!res.ok) throw new Error("Failed to remove dependency");

            // Refresh dependencies
            const dependenciesRes = await authenticatedFetch("dependencies");
            if (dependenciesRes.ok) {
                const dependenciesData = await dependenciesRes.json();
                const depMap: Record<number, number[]> = {};
                dependenciesData.dependencies.forEach((d: any) => {
                    depMap[d.id] = d.dependsOn.map((dep: any) => dep.id);
                });
                setDependencies(depMap);
            }

            toast.success("Dependency removed successfully");
        } catch (error: any) {
            toast.error(error.message);
        }
    };

    const handleOpenEditDescription = (permission: Permission) => {
        setEditDescriptionPermission(permission);
        setEditDescriptionText(permission.description ?? "");
        setIsEditDescriptionOpen(true);
    };

    const handleSaveDescription = async () => {
        if (!editDescriptionPermission) return;

        setIsSavingDescription(true);
        try {
            const res = await authenticatedFetch(`permissions/${editDescriptionPermission.id}`, {
                method: "PATCH",
                body: JSON.stringify({ description: editDescriptionText.trim() }),
            });
            if (!res.ok) throw new Error('Failed to update description');

            const data = await res.json();
            if (data.success && data.permission) {
                setAllPermissions(prev =>
                    prev.map(p => p.id === data.permission.id ? data.permission : p)
                );
                if (selectedPolicy) {
                    setSelectedPolicy(prev => prev ? {
                        ...prev,
                        permissions: prev.permissions.map(p =>
                            p.id === data.permission.id ? data.permission : p
                        ),
                    } : prev);
                }
            }

            toast.success("Description updated successfully");
            setIsEditDescriptionOpen(false);
            setEditDescriptionPermission(null);
            setEditDescriptionText("");
        } catch (error: unknown) {
            console.error("Failed to update description:", error);
            toast.error(error instanceof Error ? error.message : "Failed to update description");
        } finally {
            setIsSavingDescription(false);
        }
    };

    // --- Memoized Computations for Performance ---
    const filteredPolicies = useMemo(() =>
        policies.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase())),
        [policies, searchTerm]
    );

    const categorizedPermissions = useMemo(() => {
        const lowercasedFilter = searchPermissions.toLowerCase();

        const filtered = allPermissions.filter(permission => {
            const permissionString = `${permission.action}:${permission.resource.name}`.toLowerCase();
            const categoryMatch = (permission.resource.category || "General").toLowerCase().includes(lowercasedFilter);
            const labelMatch = formatPermissionLabel(permission).toLowerCase().includes(lowercasedFilter);
            return permissionString.includes(lowercasedFilter) || categoryMatch || labelMatch;
        });

        return filtered.reduce((acc, permission) => {
            const category = permission.resource.category || "General";
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(permission);
            return acc;
        }, {} as Record<string, Permission[]>);
    }, [allPermissions, searchPermissions]);

    const enabledCount = editedPermissionIds.size;
    const totalCount = allPermissions.length;

    const hasUnsavedChanges = useMemo(() => {
        if (!selectedPolicy) return false;
        const originalIds = new Set(selectedPolicy.permissions.map(p => p.id));
        return (
            editedPermissionIds.size !== originalIds.size ||
            Array.from(editedPermissionIds).some(id => !originalIds.has(id))
        );
    }, [selectedPolicy, editedPermissionIds]);

    const activePermission = useMemo(() => 
        allPermissions.find(p => p.id === activePermissionId),
    [activePermissionId, allPermissions]);

    const activeDependencies = useMemo(() => {
        if (!activePermissionId) return [];
        const depIds = dependencies[activePermissionId] || [];
        return depIds.map(id => allPermissions.find(p => p.id === id)).filter(Boolean) as Permission[];
    }, [activePermissionId, dependencies, allPermissions]);

    // --- Render Logic ---
    if (isLoading) {
        return <div>Loading permissions...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Permissions Management (RBAC)</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Manage policies and the permissions assigned to them.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {/* Panel 1: Policies List */}
                <div className="md:col-span-4 lg:col-span-3">
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="text-base font-semibold">Policies</CardTitle>
                                    <CardDescription>{filteredPolicies.length} user roles</CardDescription>
                                </div>
                                <Dialog open={isModalOpen} onOpenChange={(open) => {
                                    setIsModalOpen(open);
                                    if (!open) resetNewPolicyForm();
                                }}>
                                    <DialogTrigger asChild>
                                        <Button variant="outline" size="sm">
                                            <PlusCircle className="h-4 w-4 mr-1" />
                                            New
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                                        <DialogHeader>
                                            <DialogTitle>Create New Policy</DialogTitle>
                                            <DialogDescription>
                                                Policies grant permissions to every user matching an attribute.
                                            </DialogDescription>
                                        </DialogHeader>

                                        {/* Template */}
                                        <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
                                            <div className="space-y-1">
                                                <Label htmlFor="copyFrom">Copy permissions from</Label>
                                                <p className="text-xs text-muted-foreground">
                                                    Optional — reuse an existing role instead of starting from scratch.
                                                </p>
                                            </div>
                                            <Popover open={copyFromPopoverOpen} onOpenChange={setCopyFromPopoverOpen} modal={true}>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        id="copyFrom"
                                                        variant="outline"
                                                        role="combobox"
                                                        className="w-full justify-between font-normal bg-white"
                                                    >
                                                        {selectedCopySource
                                                            ? `${selectedCopySource.name} (${formatPermissionCount(selectedCopySource)})`
                                                            : "Start from scratch"}
                                                        <ChevronRight className="h-4 w-4 opacity-50 rotate-90 ml-2 shrink-0" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white" align="start">
                                                    <Command
                                                        className="max-h-[300px] [&_[data-slot=command-input-wrapper]]:focus-within:border-transparent [&_[data-slot=command-input-wrapper]]:focus-within:ring-0 [&_[data-slot=command-input-wrapper]]:border-none"
                                                        filter={(value, search) => {
                                                            if (value === "__scratch__") return 1;
                                                            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
                                                            return 0;
                                                        }}
                                                    >
                                                        <CommandInput placeholder="Search policies..." className="!border-none !outline-none !ring-0 focus:!ring-0 focus:!border-transparent focus-visible:!ring-0" />
                                                        <CommandList className="max-h-48 overflow-y-auto">
                                                            <CommandEmpty>No policies found.</CommandEmpty>
                                                            <CommandGroup>
                                                                <CommandItem
                                                                    value="__scratch__"
                                                                    onSelect={() => {
                                                                        setCopyFromPolicyId("");
                                                                        setCopyFromPopoverOpen(false);
                                                                    }}
                                                                >
                                                                    Start from scratch
                                                                </CommandItem>
                                                                {policies.map((policy) => (
                                                                    <CommandItem
                                                                        key={policy.id}
                                                                        value={policy.name}
                                                                        onSelect={() => {
                                                                            setCopyFromPolicyId(String(policy.id));
                                                                            setCopyFromPopoverOpen(false);
                                                                        }}
                                                                    >
                                                                        {policy.name} ({formatPermissionCount(policy)})
                                                                    </CommandItem>
                                                                ))}
                                                            </CommandGroup>
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>
                                            {selectedCopySource && (
                                                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                    <Shield className="h-3 w-3 shrink-0" />
                                                    {formatPermissionCount(selectedCopySource)} will be copied, dependencies included.
                                                </p>
                                            )}
                                        </div>

                                        {/* Details */}
                                        <div className="grid gap-4">
                                            <div className="grid gap-2">
                                                <Label htmlFor="name">Name</Label>
                                                <Input
                                                    id="name"
                                                    value={newPolicyData.name}
                                                    onChange={e => setNewPolicyData({ ...newPolicyData, name: e.target.value })}
                                                    placeholder="e.g., Policy: Onboarding Team"
                                                />
                                            </div>

                                            <div className="grid gap-2">
                                                <Label htmlFor="attrKey">Attribute key</Label>
                                                <Select
                                                    value={newPolicyData.attributeKey}
                                                    onValueChange={(value) => setNewPolicyData(prev => ({ ...prev, attributeKey: value }))}
                                                >
                                                    <SelectTrigger id="attrKey" className="w-full">
                                                        <SelectValue placeholder="Select an attribute key" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="userRole">userRole</SelectItem>
                                                        <SelectItem value="jobRole">jobRole</SelectItem>
                                                        <SelectItem value="all">all — every user</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <div className="grid gap-2">
                                                <Label htmlFor="attrValue">Attribute value</Label>
                                                {newPolicyData.attributeKey === 'all' ? (
                                                    <div className="space-y-1">
                                                        <Input value="all" disabled readOnly />
                                                        <p className="text-xs text-muted-foreground">This policy applies to every user.</p>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-1">
                                                        <Popover open={attributeValuePopoverOpen} onOpenChange={setAttributeValuePopoverOpen} modal={true}>
                                                            <PopoverTrigger asChild>
                                                                <Button
                                                                    id="attrValue"
                                                                    variant="outline"
                                                                    role="combobox"
                                                                    className="w-full justify-between font-normal bg-white"
                                                                >
                                                                    {isNewAttributeValue
                                                                        ? "+ Add new value"
                                                                        : finalAttributeValue || `Select a ${newPolicyData.attributeKey}...`}
                                                                    <ChevronRight className="h-4 w-4 opacity-50 rotate-90 ml-2 shrink-0" />
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white" align="start">
                                                                <Command
                                                                    className="max-h-[300px] [&_[data-slot=command-input-wrapper]]:focus-within:border-transparent [&_[data-slot=command-input-wrapper]]:focus-within:ring-0 [&_[data-slot=command-input-wrapper]]:border-none"
                                                                    filter={(value, search) => {
                                                                        if (value === "__new__") return 1;
                                                                        if (value.toLowerCase().includes(search.toLowerCase())) return 1;
                                                                        return 0;
                                                                    }}
                                                                >
                                                                    <CommandInput placeholder={`Search ${newPolicyData.attributeKey}...`} className="!border-none !outline-none !ring-0 focus:!ring-0 focus:!border-transparent focus-visible:!ring-0" />
                                                                    <CommandList className="max-h-48 overflow-y-auto">
                                                                        <CommandEmpty>No {newPolicyData.attributeKey} found.</CommandEmpty>
                                                                        <CommandGroup>
                                                                            {attributeValueOptions.map((value) => (
                                                                                <CommandItem
                                                                                    key={value}
                                                                                    value={value}
                                                                                    onSelect={() => {
                                                                                        setNewPolicyData(prev => ({ ...prev, attributeValue: value }));
                                                                                        setIsNewAttributeValue(false);
                                                                                        setAttributeValuePopoverOpen(false);
                                                                                    }}
                                                                                >
                                                                                    {value}
                                                                                </CommandItem>
                                                                            ))}
                                                                            {newPolicyData.attributeKey === 'userRole' && (
                                                                                <CommandItem
                                                                                    value="__new__"
                                                                                    onSelect={() => {
                                                                                        setNewPolicyData(prev => ({ ...prev, attributeValue: "" }));
                                                                                        setIsNewAttributeValue(true);
                                                                                        setAttributeValuePopoverOpen(false);
                                                                                    }}
                                                                                >
                                                                                    + Add new userRole
                                                                                </CommandItem>
                                                                            )}
                                                                        </CommandGroup>
                                                                    </CommandList>
                                                                </Command>
                                                            </PopoverContent>
                                                        </Popover>
                                                        {isNewAttributeValue && (
                                                            <Input
                                                                value={newAttributeValueInput}
                                                                onChange={(e) => setNewAttributeValueInput(e.target.value)}
                                                                placeholder={`Enter new ${newPolicyData.attributeKey}...`}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="grid gap-2">
                                                <Label htmlFor="description">Description</Label>
                                                <Textarea
                                                    id="description"
                                                    value={newPolicyData.description}
                                                    onChange={e => setNewPolicyData({ ...newPolicyData, description: e.target.value })}
                                                    className="h-20"
                                                    placeholder="Define what this policy allows..."
                                                />
                                            </div>
                                        </div>

                                        <DialogFooter>
                                            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                                                Cancel
                                            </Button>
                                            <Button onClick={handleCreatePolicy} disabled={isCreatingPolicy}>
                                                {isCreatingPolicy ? "Creating..." : "Create Policy"}
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            </div>
                            <div className="relative mt-3">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search policies..."
                                    className="pl-8"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="border-t">
                                {filteredPolicies.map((policy) => (
                                    <div
                                        key={policy.id}
                                        onClick={() => handleSelectPolicy(policy.id)}
                                        className={`flex items-center justify-between px-4 py-3 cursor-pointer border-b last:border-b-0 transition-colors ${selectedPolicy?.id === policy.id
                                            ? "bg-muted/60"
                                            : "hover:bg-muted/30"
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            {getPolicyIcon(policy.name)}
                                            <span className="text-sm font-medium">{policy.name}</span>
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Panel 2: Policy Details */}
                <div className="md:col-span-8 lg:col-span-9">
                    {selectedPolicy ? (
                        <div className="space-y-6">
                            {/* Header */}
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                                <div>
                                    <h2 className="text-lg font-semibold">{selectedPolicy.name}</h2>
                                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                                        <Badge
                                            variant="outline"
                                            className="text-red-600 border-red-200 bg-red-50 gap-1"
                                        >
                                            <Shield className="h-3 w-3" />
                                            Admin
                                        </Badge>
                                        <Badge variant="secondary">
                                            {selectedPolicy.attributeKey}: {selectedPolicy.attributeValue}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleCopyPolicy(selectedPolicy)}
                                        title="Create a new policy using this one's permissions as a template"
                                    >
                                        <Copy className="h-4 w-4 mr-1" />
                                        Duplicate
                                    </Button>
                                    {!protectedPolicies.includes(selectedPolicy.name) && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                            onClick={() => setIsDeleteDialogOpen(true)}
                                        >
                                            <Trash2 className="h-4 w-4 mr-1" />
                                            Delete
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* Delete Confirmation Dialog */}
                            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Delete Policy</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            Are you sure you want to delete <strong>{selectedPolicy.name}</strong>?<br /><br />
                                            This action is <strong>permanent</strong>. Once deleted, all permissions associated with this policy will be voided and users matching <strong>{selectedPolicy.attributeKey} = "{selectedPolicy.attributeValue}"</strong> will lose these permissions immediately.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleDeletePolicy();
                                            }}
                                            disabled={isDeleting}
                                            className="bg-red-600 hover:bg-red-700 text-white"
                                        >
                                            {isDeleting ? "Deleting..." : "Delete Policy"}
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>

                            {/* Description Box */}
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-blue-900 mb-1">
                                    What this role can do:
                                </h3>
                                <p className="text-sm text-blue-800">
                                    {selectedPolicy.description || "No description available for this policy."}
                                </p>
                            </div>

                            {/* Permissions */}
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-base font-semibold">Permissions</h3>
                                        {hasUnsavedChanges && (
                                            <Badge variant="destructive" className="text-xs">Unsaved changes</Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm text-muted-foreground">
                                            {enabledCount} of {totalCount} enabled
                                        </span>
                                        <Dialog open={isAddPermissionOpen} onOpenChange={(open) => {
                                            setIsAddPermissionOpen(open);
                                            if (!open) resetAddPermissionForm();
                                        }}>
                                            <DialogTrigger asChild>
                                                <Button variant="outline" size="sm">
                                                    <PlusCircle className="h-4 w-4 mr-1" />
                                                    Add Permission
                                                </Button>
                                            </DialogTrigger>
                                            <DialogContent className="sm:max-w-md">
                                                <DialogHeader>
                                                    <DialogTitle>Add Permission</DialogTitle>
                                                    <DialogDescription>
                                                        Create a new permission by selecting an action and resource.
                                                    </DialogDescription>
                                                </DialogHeader>
                                                <div className="grid gap-4 py-4">
                                                    {/* Action */}
                                                    <div className="grid gap-2">
                                                        <Label>Action</Label>
                                                        <Popover open={actionPopoverOpen} onOpenChange={setActionPopoverOpen} modal={true}>
                                                            <PopoverTrigger asChild>
                                                                <Button
                                                                    variant="outline"
                                                                    role="combobox"
                                                                    className="w-full justify-between font-normal"
                                                                >
                                                                    {isNewAction
                                                                        ? "+ Add new action"
                                                                        : addPermissionForm.action || "Select an action..."}
                                                                    <ChevronRight className="h-4 w-4 opacity-50 rotate-90 ml-2 shrink-0" />
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white" align="start">
                                                                <Command
                                                                    className="max-h-[300px] [&_[data-slot=command-input-wrapper]]:focus-within:border-transparent [&_[data-slot=command-input-wrapper]]:focus-within:ring-0 [&_[data-slot=command-input-wrapper]]:border-none"
                                                                    filter={(value, search) => {
                                                                        if (value === "__new__") return 1;
                                                                        if (value.toLowerCase().includes(search.toLowerCase())) return 1;
                                                                        return 0;
                                                                    }}
                                                                >
                                                                    <CommandInput placeholder="Search actions..." className="!border-none !outline-none !ring-0 focus:!ring-0 focus:!border-transparent focus-visible:!ring-0" />
                                                                    <CommandList className="max-h-48 overflow-y-auto">
                                                                        <CommandEmpty>No actions found.</CommandEmpty>
                                                                        <CommandGroup>
                                                                            {actions.map((action) => (
                                                                                <CommandItem
                                                                                    key={action}
                                                                                    value={action}
                                                                                    onSelect={() => {
                                                                                        setAddPermissionForm(prev => ({ ...prev, action }));
                                                                                        setActionPopoverOpen(false);
                                                                                    }}
                                                                                >
                                                                                    {action}
                                                                                </CommandItem>
                                                                            ))}
                                                                            <CommandItem
                                                                                value="__new__"
                                                                                onSelect={() => {
                                                                                    setAddPermissionForm(prev => ({ ...prev, action: "__new__" }));
                                                                                    setActionPopoverOpen(false);
                                                                                }}
                                                                            >
                                                                                + Add new action
                                                                            </CommandItem>
                                                                        </CommandGroup>
                                                                    </CommandList>
                                                                </Command>
                                                            </PopoverContent>
                                                        </Popover>
                                                        {isNewAction && (
                                                            <div className="grid gap-2 mt-1">
                                                                <Input
                                                                    value={addPermissionForm.newAction}
                                                                    onChange={(e) =>
                                                                        setAddPermissionForm(prev => ({
                                                                            ...prev,
                                                                            newAction: e.target.value,
                                                                        }))
                                                                    }
                                                                    placeholder="Enter new action name..."
                                                                />
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Resource */}
                                                    <div className="grid gap-2">
                                                        <Label>Resource</Label>
                                                        <Popover open={resourcePopoverOpen} onOpenChange={setResourcePopoverOpen} modal={true}>
                                                            <PopoverTrigger asChild>
                                                                <Button
                                                                    variant="outline"
                                                                    role="combobox"
                                                                    className="w-full justify-between font-normal"
                                                                >
                                                                    {resources.find(r => String(r.id) === addPermissionForm.resourceId)?.name ||
                                                                        (addPermissionForm.resourceId === "__new__" ? "+ Add new resource" : "Select a resource...")}
                                                                    <ChevronRight className="h-4 w-4 opacity-50 rotate-90 ml-2 shrink-0" />
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white" align="start">
                                                                <Command
                                                                    className="max-h-[300px] [&_[data-slot=command-input-wrapper]]:focus-within:border-transparent [&_[data-slot=command-input-wrapper]]:focus-within:ring-0 [&_[data-slot=command-input-wrapper]]:border-none"
                                                                    filter={(value, search) => {
                                                                        if (value === "__new__") return 1;
                                                                        if (value.toLowerCase().includes(search.toLowerCase())) return 1;
                                                                        return 0;
                                                                    }}
                                                                >
                                                                    <CommandInput placeholder="Search resources..." className="!border-none !outline-none !ring-0 focus:!ring-0 focus:!border-transparent focus-visible:!ring-0" />
                                                                    <CommandList className="max-h-48 overflow-y-auto">
                                                                        <CommandEmpty>No resources found.</CommandEmpty>
                                                                        <CommandGroup>
                                                                            {resources.map((resource) => (
                                                                                <CommandItem
                                                                                    key={resource.id}
                                                                                    value={resource.name}
                                                                                    onSelect={() => {
                                                                                        setAddPermissionForm(prev => ({
                                                                                            ...prev,
                                                                                            resourceId: String(resource.id),
                                                                                            category: "",
                                                                                            newCategory: "",
                                                                                        }));
                                                                                        setResourcePopoverOpen(false);
                                                                                    }}
                                                                                >
                                                                                    {resource.name}
                                                                                </CommandItem>
                                                                            ))}
                                                                            <CommandItem
                                                                                value="__new__"
                                                                                onSelect={() => {
                                                                                    setAddPermissionForm(prev => ({
                                                                                        ...prev,
                                                                                        resourceId: "__new__",
                                                                                        category: "",
                                                                                        newCategory: "",
                                                                                    }));
                                                                                    setResourcePopoverOpen(false);
                                                                                }}
                                                                            >
                                                                                + Add new resource
                                                                            </CommandItem>
                                                                        </CommandGroup>
                                                                    </CommandList>
                                                                </Command>
                                                            </PopoverContent>
                                                        </Popover>
                                                    </div>

                                                    {/* New Resource Fields */}
                                                    {isNewResource && (
                                                        <>
                                                            <div className="grid gap-2">
                                                                <Label htmlFor="new-res-name">Resource Name</Label>
                                                                <Input
                                                                    id="new-res-name"
                                                                    value={addPermissionForm.newResourceName}
                                                                    onChange={(e) =>
                                                                        setAddPermissionForm(prev => ({
                                                                            ...prev,
                                                                            newResourceName: e.target.value,
                                                                        }))
                                                                    }
                                                                    placeholder="e.g., custom_module"
                                                                />
                                                            </div>
                                                            <div className="grid gap-2">
                                                                <Label htmlFor="new-res-desc">Description</Label>
                                                                <Input
                                                                    id="new-res-desc"
                                                                    value={addPermissionForm.newResourceDescription}
                                                                    onChange={(e) =>
                                                                        setAddPermissionForm(prev => ({
                                                                            ...prev,
                                                                            newResourceDescription: e.target.value,
                                                                        }))
                                                                    }
                                                                    placeholder="Optional description..."
                                                                />
                                                            </div>
                                                            <div className="grid gap-2">
                                                                <Label htmlFor="new-res-category">Category</Label>
                                                                <Select
                                                                    value={addPermissionForm.category}
                                                                    onValueChange={(value) =>
                                                                        setAddPermissionForm(prev => ({
                                                                            ...prev,
                                                                            category: value,
                                                                            newCategory: "",
                                                                        }))
                                                                    }
                                                                >
                                                                    <SelectTrigger id="new-res-category">
                                                                        <SelectValue placeholder="Select a category..." />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {existingCategories.map((cat) => (
                                                                            <SelectItem key={cat} value={cat}>
                                                                                {cat}
                                                                            </SelectItem>
                                                                        ))}
                                                                        <SelectItem value="__new__">+ Add new category</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                            {isNewCategory && (
                                                                <div className="grid gap-2">
                                                                    <Label htmlFor="new-category-name">New Category Name</Label>
                                                                    <Input
                                                                        id="new-category-name"
                                                                        value={addPermissionForm.newCategory}
                                                                        onChange={(e) =>
                                                                            setAddPermissionForm(prev => ({
                                                                                ...prev,
                                                                                newCategory: e.target.value,
                                                                            }))
                                                                        }
                                                                        placeholder="e.g., Custom Modules"
                                                                    />
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                    <div className="grid gap-2">
                                                        <Label htmlFor="perm-description">Description</Label>
                                                        <Textarea
                                                            id="perm-description"
                                                            value={addPermissionForm.permissionDescription}
                                                            onChange={(e) =>
                                                                setAddPermissionForm(prev => ({
                                                                    ...prev,
                                                                    permissionDescription: e.target.value,
                                                                }))
                                                            }
                                                            placeholder="What can the user do when this is enabled? Shown under the permission title in Settings > Permissions."
                                                            className="h-20"
                                                        />
                                                    </div>
                                                </div>
                                                <DialogFooter>
                                                    <Button
                                                        onClick={handleAddPermission}
                                                        disabled={isAddingPermission}
                                                    >
                                                        {isAddingPermission ? "Adding..." : "Add Permission"}
                                                    </Button>
                                                </DialogFooter>
                                            </DialogContent>
                                        </Dialog>
                                    </div>
                                </div>

                                <div className="relative mb-4">
                                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search permissions..."
                                        className="pl-8"
                                        value={searchPermissions}
                                        onChange={(e) => setSearchPermissions(e.target.value)}
                                    />
                                </div>

                                <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                                    {Object.keys(categorizedPermissions).length > 0 ? (
                                        Object.entries(categorizedPermissions).map(([category, permissions]) => (
                                            <div key={category}>
                                                <h3 className="text-md font-semibold mb-3 text-muted-foreground">
                                                    {category}
                                                </h3>
                                                <div className="space-y-2">
                                                    {permissions.map((permission) => {
                                                        const description = permission.description;
                                                        return (
                                                        <div
                                                            key={permission.id}
                                                            className="p-3 border rounded-lg bg-card group"
                                                        >
                                                            <div className="flex items-center justify-between gap-3">
                                                                <span className="text-sm font-medium">
                                                                    {formatPermissionLabel(permission)}
                                                                </span>
                                                                <div className="flex items-center gap-3">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                                                                        title={description ? "Edit description" : "Add description"}
                                                                        aria-label={description ? "Edit description" : "Add description"}
                                                                        onClick={() => handleOpenEditDescription(permission)}
                                                                    >
                                                                        <SquarePen className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                        onClick={() => {
                                                                            setActivePermissionId(permission.id);
                                                                            setIsDependencyModalOpen(true);
                                                                        }}
                                                                    >
                                                                        <Link className="h-4 w-4" />
                                                                    </Button>
                                                                    <Switch
                                                                        checked={editedPermissionIds.has(permission.id)}
                                                                        onCheckedChange={(checked) =>
                                                                            handlePermissionToggle(permission.id, !!checked)
                                                                        }
                                                                        disabled={lockedPermissions.has(permission.id)}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {description && (
                                                                <p className="text-xs text-muted-foreground mt-1.5">
                                                                    {description}
                                                                </p>
                                                            )}
                                                        </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center text-muted-foreground py-8">
                                            No permissions available.
                                        </div>
                                    )}
                                </div>

                                {/* Scoped Dependency Modal */}
                                <Dialog open={isDependencyModalOpen} onOpenChange={(open) => {
                                    setIsDependencyModalOpen(open);
                                    if (!open) {
                                        setActivePermissionId(null);
                                        setNewDependencyId("");
                                    }
                                }}>
                                    <DialogContent className="max-w-md">
                                        <DialogHeader>
                                            <DialogTitle>Manage Dependencies</DialogTitle>
                                            <DialogDescription>
                                                Rules for <span className="font-semibold text-foreground font-mono">"{activePermission ? `${activePermission.action}:${activePermission.resource.name}` : ""}"</span>
                                            </DialogDescription>
                                        </DialogHeader>

                                        {activePermission && (
                                            <div className="space-y-6 py-4">
                                                <div className="space-y-2">
                                                    <Label className="text-xs font-bold text-muted-foreground uppercase">Add Dependency</Label>
                                                    <div className="flex gap-2">
                                                        <div className="flex-1">
                                                            <Popover open={dependencySelectPopoverOpen} onOpenChange={setDependencySelectPopoverOpen} modal={true}>
                                                                <PopoverTrigger asChild>
                                                                    <Button
                                                                        variant="outline"
                                                                        role="combobox"
                                                                        className="w-full justify-between font-normal"
                                                                    >
                                                                        {newDependencyId 
                                                                            ? formatPermissionLabel(allPermissions.find(p => String(p.id) === newDependencyId)!)
                                                                            : "Select permission..."}
                                                                        <ChevronRight className="h-4 w-4 opacity-50 rotate-90 ml-2 shrink-0" />
                                                                    </Button>
                                                                </PopoverTrigger>
                                                                <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white" align="start">
                                                                    <Command
                                                                        className="max-h-[300px] [&_[data-slot=command-input-wrapper]]:focus-within:border-transparent [&_[data-slot=command-input-wrapper]]:focus-within:ring-0 [&_[data-slot=command-input-wrapper]]:border-none"
                                                                    >
                                                                        <CommandInput placeholder="Search label or code..." className="!border-none !outline-none !ring-0 focus:!ring-0 focus:!border-transparent focus-visible:!ring-0" />
                                                                        <CommandList className="max-h-48 overflow-y-auto">
                                                                            <CommandEmpty>No permissions found.</CommandEmpty>
                                                                            <CommandGroup>
                                                                                {allPermissions
                                                                                    .filter(p => p.id !== activePermissionId && !dependencies[activePermissionId!]?.includes(p.id))
                                                                                    .map((p) => (
                                                                                        <CommandItem
                                                                                            key={p.id}
                                                                                            value={`${formatPermissionLabel(p)} ${p.action}:${p.resource.name} ${p.id}`}
                                                                                            onSelect={() => {
                                                                                                setNewDependencyId(String(p.id));
                                                                                                setDependencySelectPopoverOpen(false);
                                                                                            }}
                                                                                            className="flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                                                                                        >
                                                                                            <span className="font-medium">{formatPermissionLabel(p)}</span>
                                                                                            <span className="text-[10px] text-muted-foreground font-mono">{p.action}:{p.resource.name}</span>
                                                                                        </CommandItem>
                                                                                    ))}
                                                                            </CommandGroup>
                                                                        </CommandList>
                                                                    </Command>
                                                                </PopoverContent>
                                                            </Popover>
                                                        </div>
                                                        <Button onClick={handleAddDependency} disabled={isSavingDependency || !newDependencyId}>
                                                            {isSavingDependency ? "..." : <PlusCircle className="h-4 w-4" />}
                                                        </Button>
                                                    </div>
                                                    <p className="text-[11px] text-muted-foreground italic">Enabling this permission will automatically enable and lock the selected ones.</p>
                                                </div>

                                                <div className="space-y-3 pt-4 border-t">
                                                    <Label className="text-xs font-bold text-muted-foreground uppercase">Active Dependencies ({activeDependencies.length})</Label>
                                                    {activeDependencies.length > 0 ? (
                                                        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                                                            {activeDependencies.map(dep => (
                                                                <div key={dep.id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/10">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-sm font-medium">{formatPermissionLabel(dep)}</span>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-[10px] text-muted-foreground bg-muted px-1 rounded font-mono">{dep.action}:{dep.resource.name}</span>
                                                                            <span className="text-[10px] text-muted-foreground/60">{dep.resource.category || "General"}</span>
                                                                        </div>
                                                                    </div>
                                                                    <Button 
                                                                        variant="ghost" 
                                                                        size="sm" 
                                                                        onClick={() => handleRemoveDependency(activePermission.id, dep.id)}
                                                                        className="hover:bg-red-50 hover:text-red-500 h-8 w-8 p-0"
                                                                    >
                                                                        <Unlink className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="py-8 border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-muted-foreground opacity-50">
                                                            <Link className="h-6 w-6 mb-2" />
                                                            <span className="text-sm">No dependencies defined</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <DialogFooter>
                                            <Button variant="outline" onClick={() => setIsDependencyModalOpen(false)}>Close</Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>

                                {/* Edit permission description modal */}
                                <Dialog open={isEditDescriptionOpen} onOpenChange={(open) => {
                                    setIsEditDescriptionOpen(open);
                                    if (!open) {
                                        setEditDescriptionPermission(null);
                                        setEditDescriptionText("");
                                    }
                                }}>
                                    <DialogContent className="max-w-md">
                                        <DialogHeader>
                                            <DialogTitle>
                                                {editDescriptionPermission?.description ? "Edit permission description" : "Add permission description"}
                                            </DialogTitle>
                                            <DialogDescription>
                                                Shown under the permission title in Settings &gt; Permissions.
                                            </DialogDescription>
                                        </DialogHeader>
                                        {editDescriptionPermission && (
                                            <div className="space-y-4 py-4">
                                                <div className="flex flex-col gap-0.5">
                                                    <span className="text-sm font-medium">{formatPermissionLabel(editDescriptionPermission)}</span>
                                                    <span className="text-[10px] text-muted-foreground font-mono">
                                                        {editDescriptionPermission.action}:{editDescriptionPermission.resource.name}
                                                    </span>
                                                </div>
                                                <div className="grid gap-2">
                                                    <Label htmlFor="edit-perm-description">Description</Label>
                                                    <Textarea
                                                        id="edit-perm-description"
                                                        value={editDescriptionText}
                                                        onChange={(e) => setEditDescriptionText(e.target.value)}
                                                        placeholder="What can the user do when this is enabled? Leave empty to remove the description."
                                                        className="h-24"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <DialogFooter>
                                            <Button variant="outline" onClick={() => setIsEditDescriptionOpen(false)}>Cancel</Button>
                                            <Button
                                                onClick={handleSaveDescription}
                                                disabled={isSavingDescription}
                                            >
                                                {isSavingDescription ? "Saving..." : "Save"}
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-3">
                                <Button
                                    onClick={handleSaveChanges}
                                    disabled={isSaving}
                                >
                                    {isSaving ? "Saving..." : "Save Changes"}
                                </Button>
                            </div>

                            {/* Policy Assignment */}
                            <div className="border-t pt-6">
                                <h4 className="text-sm font-semibold mb-1">Policy Assignment</h4>
                                <p className="text-sm text-muted-foreground">
                                    {selectedPolicy.attributeKey === 'all'
                                        ? "This policy is automatically assigned to every user."
                                        : `This policy is automatically assigned to users with the ${selectedPolicy.attributeValue} role. To change a user's permissions, edit their User Role in the Edit Person modal.`}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full rounded-lg border-2 border-dashed border-gray-300 min-h-[400px]">
                            <div className="text-center">
                                <h3 className="text-lg font-semibold">Select a Policy</h3>
                                <p className="text-muted-foreground">Choose a policy from the list on the left to view and edit its permissions.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AbacManagementPage;
